import { AvailableExtra, Currency } from '~/lib/types/basket-types';
import { P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { QuoteIncludedCoverButParcelValueIsHigher, IsSpecialQuotePageScenario } from './is-special-quote-scenario';
import { toDisplayCurrency } from '~/lib/prices';

type props = {
  currency: Currency;
  option: AvailableExtra;
  parcelValue: number;
  includedFreeOrBaseProtectionAmount: number;
  maxProtection: number;
  dataTestId: string;
  isFullyProtectedByIncludedOrBaseProtection: boolean;
  protectionNotAvailable: boolean;
  showMessage: boolean;
};

export const ParcelProtectionTitle = ({
  currency,
  option,
  parcelValue,
  maxProtection,
  dataTestId,
  includedFreeOrBaseProtectionAmount,
  isFullyProtectedByIncludedOrBaseProtection,
  showMessage
}: props) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const baseSelectedAndDoesntFullyProtect = IsSpecialQuotePageScenario(
    true,
    option,
    parcelValue,
    option?.metadata?.maxProtection || 0
  );
  const includedDoesntFullyProtect = QuoteIncludedCoverButParcelValueIsHigher(
    true,
    option,
    parcelValue,
    includedFreeOrBaseProtectionAmount
  );

  if (parcelValue === 0 && includedFreeOrBaseProtectionAmount) {
    return (
      <P2GText dataTestId={`${dataTestId}-title`} variant="h3" component="h3">
        {t('PROTECTION_TITLE_INCLUDED', {
          defaultValue: '{{includedProtection}} Parcel Protection Included',
          includedProtection: toDisplayCurrency(currency, includedFreeOrBaseProtectionAmount, 0)
        })}
      </P2GText>
    );
  }

  if (baseSelectedAndDoesntFullyProtect || includedDoesntFullyProtect) {
    return (
      <>
        <P2GText dataTestId={`${dataTestId}-title`} variant="h3" component="h3">
          {t('PROTECTION_TITLE_OPTED_IN', {
            defaultValue: 'Fully Protect Your Parcel'
          })}
        </P2GText>
        <P2GText>
          {t('PROTECTION_MESSAGE_OPTED_IN', {
            defaultValue:
              'Your parcel value is higher than the {{includedProtection}} included protection. Would you like to fully protect your parcel?',
            includedProtection: toDisplayCurrency(
              currency,
              baseSelectedAndDoesntFullyProtect
                ? option.metadata?.maxProtection || 0
                : includedFreeOrBaseProtectionAmount || 0,
              0
            )
          })}
        </P2GText>
      </>
    );
  }

  return (
    <>
      <P2GText dataTestId={`${dataTestId}-title`} variant="h3" component="h3">
        {includedFreeOrBaseProtectionAmount || isFullyProtectedByIncludedOrBaseProtection
          ? t('PROTECTION_TITLE_INCLUDED', {
              defaultValue: '{{includedProtection}} Parcel Protection Included',
              includedProtection: toDisplayCurrency(currency, includedFreeOrBaseProtectionAmount, 0)
            })
          : t('PROTECTION_TITLE', {
              defaultValue: 'Get Peace of Mind, Protect Your Delivery'
            })}
      </P2GText>
      {showMessage && (
        <P2GText>
          {t('PROTECTION_MESSAGE', {
            defaultValue:
              'Should the unexpected happen, you can protect your parcel up to the value of {{maxProtection}}.',
            maxProtection: toDisplayCurrency(currency, maxProtection, 0)
          })}
        </P2GText>
      )}
    </>
  );
};
